import { IdSchemaEnum } from "../../../Configurator/Enums/IdSchemaEnum";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import { ErrorHandlingModeType } from "../../../Configurator/Types/ConfiguratorTypes";
import { ErrorCodes } from "../../../Exception/ErrorCodes";
import { ErrorStatuses } from "../../../Exception/ErrorStatuses";
import FansUnitedSdkException from "../../../Exception/FansUnitedSdkException";
import StandardFansUnitedException from "../../../Exception/StandardFansUnitedException";
import { ErrorMessages } from "../../../Global/Messages/Messages";
import IdMappingService from "../../../IdMapping/IdMappingService";
import IdMappingModel from "../../../IdMapping/Models/IdMappingModel";
import Football from "../../Football/Football";
import PlayerDetailedStatsModel from "../Models/PlayerDetailedStatsModel";
import PlayerPointsModel from "../Models/PlayerPointsModel";

export default class FantasyService {
  private idMapping: IdMappingService = null;
  private idSchema: string = null;
  private errorHandlingModel: ErrorHandlingModeType = null;
  private football: Football = null;

  constructor(config: SDKConfigurationModel) {
    this.idSchema = config.idSchema;
    this.errorHandlingModel = config.errorHandlingMode;
    this.football = new Football(config);
    this.idMapping = new IdMappingService(config);
  }

  public remapCompetitions = async (competitionIds: string[]) => {
    const idsObj = { competition: competitionIds };
    const result = await this.idMapping.idMappingFacade.getEntitiesByIds(
      idsObj,
      IdSchemaEnum.NATIVE,
      this.idSchema
    );

    return result.competition;
  };

  public remapPlayers = async (
    playerIds: string[],
    fromSchema: string,
    toSchema: string,
    playersRoles: Map<string, string> | null
  ): Promise<string[]> => {
    const idsObj = { player: Array.from(playerIds) };
    const resultMap = await this.idMapping.idMappingFacade.getEntitiesByIdsMap(
      idsObj,
      fromSchema,
      toSchema
    );

    // If playersRoles exists, create a new Map with remapped IDs
    if (playersRoles) {
      const remappedRoles = new Map<string, string>();
      for (const [oldId, role] of Array.from(playersRoles.entries())) {
        const newId = resultMap.get(oldId);
        if (newId) {
          remappedRoles.set(newId, role);
        }
      }
      // Clear and update the original map with new mappings
      playersRoles.clear();
      for (const [newId, role] of Array.from(remappedRoles.entries())) {
        playersRoles.set(newId, role);
      }
    }

    return Array.from(resultMap.values());
  };

  public addPlayerModels = async (data: PlayerPointsModel[]) => {
    const playerIds = data.map((p) => p.playerId);
    const playersMap =
      await this.football.footballFacade.getPlayersMapWithNativeIds(playerIds);
    data.forEach((v) => {
      v.playerModel = Object.keys(playersMap).length
        ? playersMap[v.playerId] || null
        : null;
      v.playerId = v.playerModel?.id || v.playerId;
    });

    return data;
  };

  public remapPlayerIdToNative = async (id: string) => {
    const data = await this.idMapping.getEntityIdBySchemaId("player", id);

    if (data) {
      const idMappingEntity = data as IdMappingModel;

      return idMappingEntity.id;
    }

    // No mapping found in local cache or in Id Mapping API
    this.throwException(
      ErrorCodes.NOT_FOUND,
      ErrorStatuses.ID_MAPPING_NOT_FOUND,
      ErrorMessages.ID_MAPPING_NOT_FOUND + id
    );
  };

  public addMatchModels = async (data: PlayerDetailedStatsModel[]) => {
    const matchIds = data.map((v) => v.matchId);
    const matchesMap =
      await this.football.footballFacade.getMatchesMapWithNativeIds(matchIds);
    data.forEach((v) => {
      v.matchModel = Object.keys(matchesMap).length
        ? matchesMap[v.matchId] || null
        : null;
      v.matchId = v.matchModel?.id || v.matchId;
    });

    return data;
  };

  public throwException = (code: number, status: string, message: string) => {
    if (this.errorHandlingModel === "standard") {
      throw new StandardFansUnitedException(code, status, message);
    }

    throw new FansUnitedSdkException(code, status, message);
  };
}
